import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Extracted repeated class strings into variables for consistency
const raisedBorder =
  "border-t-[#767676] border-l-[#767676] border-r-[#212121] border-b-[#212121]";

const buttonVariants = cva(
  // Base classes for all buttons
  [
    "inline-flex items-center justify-center gap-2",
    "whitespace-break-spaces uppercase font-sans",
    "no-underline hover:no-underline active:no-underline", // Link-like styles
    "tap-highlight-transparent", // Mobile tap highlight
    "focus-visible:outline-auto focus-visible:outline-black focus-visible:outline-4",
    "border-[1.6px]",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        default: `bg-interactive-bg text-black hover:bg-interactive-hover visited:text-black ${raisedBorder}`,
        black: `bg-black text-white hover:bg-gray-800 hover:text-white visited:text-white ${raisedBorder}`,
      },
      visualState: {
        normal:
          "cursor-pointer shadow-basic active:shadow-none active:translate-y-0.5 active:translate-x-0.5 disabled:pointer-events-none disabled:cursor-auto disabled:opacity-50 disabled:active:translate-y-0 disabled:active:translate-x-0",
        active: "cursor-pointer shadow-basic", // Looks active but doesn't move on click
        fake: "pointer-events-none cursor-default shadow-basic", // Display-only, non-interactive with shadow
      },
      size: {
        default: "px-1 py-1 text-base min-h-[44px]",
        lg: "px-4 py-1 text-lg min-h-[44px]",
        xl: "px-4 py-1 text-xl min-h-[44px]",
        "2xl": "px-4 py-1 text-2xl min-h-[44px]",
        "4xl": "px-4 py-1 text-4xl min-h-[44px]",
        "7xl": "px-4 py-1 text-7xl leading-none min-h-[64px] min-w-[64px]", // Arrow buttons
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      visualState: "normal",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, visualState, asChild = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, visualState, className })
        )}
        ref={ref}
        {...(props as any)}
      />
    );
  }
);
Button.displayName = "Button";

export default Button;
